/**
 * Admin · Neighborhood Intelligence — index.
 *
 * Searchable, sortable list of neighborhoods (Areas) with headline KPIs and an
 * Activity Score. Drill into one for the full dashboard. Admin chrome is in
 * English to match the rest of the admin panel; neighborhood data shows both
 * EN/AR names.
 */

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../../utils/apiClient';
import {
  fetchNeighborhoods,
  type NeighborhoodListItem,
  type NeighborhoodPeriod,
} from '../../../services/neighborhoodService';

interface City { id: string; nameEn: string; nameAr?: string; slug: string }

const PERIODS: { value: NeighborhoodPeriod; label: string }[] = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '1y', label: 'Last 12 months' },
];

function scoreColor(score: number): string {
  if (score >= 70) return 'bg-green-100 text-green-700';
  if (score >= 40) return 'bg-amber-100 text-amber-700';
  return 'bg-gray-100 text-gray-500';
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
      <div className="text-xs text-gray-400">{label}</div>
      <div className="text-xl font-semibold text-gray-900">{value}</div>
    </div>
  );
}

export default function AdminNeighborhoodsPage() {
  const [items, setItems] = useState<NeighborhoodListItem[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [cityId, setCityId] = useState('');
  const [period, setPeriod] = useState<NeighborhoodPeriod>('30d');
  const [sort, setSort] = useState('activity');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    apiRequest<{ data?: City[]; cities?: City[] }>('/locations/cities')
      .then((res) => setCities(res.data ?? res.cities ?? []))
      .catch(() => setCities([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchNeighborhoods({ search: search || undefined, cityId: cityId || undefined, period, sort, page, pageSize: 20 });
      setItems(res.data);
      setTotalPages(res.meta.totalPages);
      setTotal(res.meta.total);
    } catch {
      setError('Failed to load neighborhoods.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [search, cityId, period, sort, page]);

  useEffect(() => { load(); }, [load]);

  // Debounce the free-text search.
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); setSearch(searchInput); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const topActive = items.reduce((acc, i) => acc + i.activeListings, 0);
  const topViews = items.reduce((acc, i) => acc + i.propertyViews, 0);

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link to="/admin" className="text-sm text-gray-500 hover:text-[#003930] inline-flex items-center gap-1.5 mb-3">
            <i className="ri-arrow-left-line" /> Back to admin
          </Link>
          <h1 className="text-2xl font-bold text-[var(--pv-color-primary)]">Neighborhood Intelligence</h1>
          <p className="text-gray-500 mt-1">Per-area inventory, demand, engagement, agents & local news - the data engine for neighborhood insights.</p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Controls */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search neighborhood…"
              className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#B7DDD7]"
            />
          </div>
          <select value={cityId} onChange={(e) => { setPage(1); setCityId(e.target.value); }} className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-white">
            <option value="">All cities</option>
            {cities.map((c) => <option key={c.id} value={c.id}>{c.nameEn}</option>)}
          </select>
          <select value={period} onChange={(e) => setPeriod(e.target.value as NeighborhoodPeriod)} className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-white">
            {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-white">
            <option value="activity">Sort: Activity score</option>
            <option value="listings">Sort: Active listings</option>
            <option value="views">Sort: Property views</option>
          </select>
        </div>

        {/* Page-level stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
          <Stat label="Neighborhoods" value={total} />
          <Stat label="Active listings (page)" value={topActive.toLocaleString()} />
          <Stat label="Property views (page)" value={topViews.toLocaleString()} />
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="py-16 text-center text-gray-400"><i className="ri-loader-4-line animate-spin text-2xl" /></div>
          ) : error ? (
            <div className="py-16 text-center text-red-500">{error}</div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-gray-400">No neighborhoods found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                    <th className="px-4 py-3 font-medium">Neighborhood</th>
                    <th className="px-4 py-3 font-medium">City</th>
                    <th className="px-4 py-3 font-medium text-right">Listings</th>
                    <th className="px-4 py-3 font-medium text-right">Property views</th>
                    <th className="px-4 py-3 font-medium text-right">Area views</th>
                    <th className="px-4 py-3 font-medium text-right">News</th>
                    <th className="px-4 py-3 font-medium text-center">Activity</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((n) => (
                    <tr key={n.areaId} className="border-b border-gray-50 hover:bg-[var(--pv-color-surface-strong)]">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{n.nameEn}</div>
                        {n.nameAr && <div className="text-xs text-gray-400" dir="rtl">{n.nameAr}</div>}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{n.cityNameEn || '-'}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{n.activeListings.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{n.propertyViews.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{n.areaViews.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{n.newsCount}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-block min-w-[2.5rem] px-2 py-1 rounded-lg text-xs font-bold ${scoreColor(n.activityScore)}`}>{n.activityScore}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link to={`/admin/neighborhoods/${n.areaId}`} className="text-[#003930] hover:underline inline-flex items-center gap-1">
                          Open <i className="ri-arrow-right-line" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="px-3 py-2 rounded-lg border border-gray-200 text-sm disabled:opacity-40 hover:bg-gray-50">Prev</button>
            <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="px-3 py-2 rounded-lg border border-gray-200 text-sm disabled:opacity-40 hover:bg-gray-50">Next</button>
          </div>
        )}
      </main>
    </div>
  );
}
